import { object, string } from "yup";
import { emailOptional, nonEmptyString, unit } from "@covertree/utils";
import { AdditionalInterestType } from "@covertree/new-business-graphql";

import { defaultMaxInterests } from "./consts";

/**
 * @param interestsCountByType - interests already held, per type
 * @param selectedInterestType - type of the interest being edited, if any
 * @param maxInterests - per-type caps; checkout varies them by payment plan
 */
export const createAdditionalInterestValidator = (
  interestsCountByType: Record<AdditionalInterestType, number>,
  selectedInterestType?: AdditionalInterestType,
  maxInterests: Record<AdditionalInterestType, number> = defaultMaxInterests
) =>
  object({
    name: string()
      .transform((value, originalValue) => {
        if (typeof originalValue === "string") {
          return originalValue.trim();
        }

        if (originalValue && typeof originalValue === "object") {
          const raw = (originalValue as { value?: string })?.value ?? "";
          return raw.trim();
        }

        return typeof value === "string" ? value.trim() : value;
      })
      .typeError("Lender name is required")
      .required("Lender name is required")
      .test({
        message: "Lender name is required",
        test: (value) => typeof value === "string" && value.trim().length > 0,
      }),
    type: string()
      .required("Type is required")
      .oneOf(
        Object.values(AdditionalInterestType),
        "Please select a valid interest type"
      )
      .test(
        "interest-limit",
        "Too many interested parties added for this type",
        (value) => {
          if (!value) return true;
          let interestCount =
            interestsCountByType[value as AdditionalInterestType] || 0;
          // On edit mode, allow the same type to be added again
          if (selectedInterestType && value === selectedInterestType) {
            interestCount -= 1;
          }
          return interestCount < maxInterests[value as AdditionalInterestType];
        }
      ),
    streetAddress: nonEmptyString("Street address").required(
      "Street address is required"
    ),
    lotUnit: unit().nullable().optional(),
    city: nonEmptyString("City").required("City is required"),
    state: object({
      label: string().required(),
      value: string().required(),
    })
      .typeError("State is required")
      .required("State is required")
      .test("state-required", "State is required", (value) => {
        return !!value?.label && !!value?.value;
      }),
    zipCode: string()
      .required("Zip code is required")
      .test(
        "len",
        "Zip code must be 5 or 9 digits",
        (val) => val?.length === 5 || val?.length === 9
      ),
    email: emailOptional(),
    accountNumber: string()
      .max(25, "Loan / account number is too long")
      .nullable()
      .optional(),
  }).required();
